<template>
  <div class="pl" :class="{ 'pl--error': error }">
    <svg
      ref="svgRef"
      class="pl__board"
      viewBox="0 0 300 300"
      role="img"
      :aria-label="ariaLabel"
      @pointerdown="onDown"
      @pointermove="onMove"
      @pointerup="onUp"
      @pointercancel="onUp"
    >
      <polyline v-if="path.length" class="pl__line" :points="linePoints" />
      <g v-for="(d, i) in DOTS" :key="i">
        <circle class="pl__hit" :cx="d.x" :cy="d.y" r="44" />
        <circle class="pl__dot" :class="{ 'is-on': seq.includes(i) }" :cx="d.x" :cy="d.y" r="14" />
        <circle v-if="seq.includes(i)" class="pl__core" :cx="d.x" :cy="d.y" r="6" />
      </g>
    </svg>
    <p class="pl__hint" aria-live="polite">{{ error || hint }}</p>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue';

// 260922 마이노트(쉼) 패턴 잠금 — 대표 "지문이나 패턴인식도 되게". 3×3 점을 이어 그린 순서(0~8)를 숫자열로 만들어
// 기존 웰다잉 비밀번호(bcrypt, 서버 검증)와 같은 경로에 태운다 = BE·DDL 변경 0. 최소 4점. 지문은 네이티브 브릿지 별건.
const DOTS = [0, 1, 2].flatMap(r => [0, 1, 2].map(c => ({ x: 60 + c * 90, y: 60 + r * 90 })));
const MIN_POINTS = 4;
const HIT_R = 34;

const props = defineProps({
  hint: { type: String, default: '점 4개 이상을 이어서 그려 주세요.' },
  ariaLabel: { type: String, default: '패턴 입력판' }
});
const emits = defineEmits(['complete', 'change']);

const svgRef = ref(null);
const seq = ref([]);
const cursor = ref(null);
const drawing = ref(false);
const error = ref('');

const path = computed(() => {
  const pts = seq.value.map(i => DOTS[i]);
  return drawing.value && cursor.value ? [...pts, cursor.value] : pts;
});
const linePoints = computed(() => path.value.map(p => `${p.x},${p.y}`).join(' '));

// 화면 좌표 → viewBox 좌표 (svg 가 반응형이라 스케일 보정)
const toLocal = e => {
  const r = svgRef.value.getBoundingClientRect();
  return { x: ((e.clientX - r.left) / r.width) * 300, y: ((e.clientY - r.top) / r.height) * 300 };
};
const hit = p => DOTS.findIndex(d => (d.x - p.x) ** 2 + (d.y - p.y) ** 2 <= HIT_R ** 2);

const onDown = e => {
  error.value = '';
  seq.value = [];
  drawing.value = true;
  svgRef.value.setPointerCapture?.(e.pointerId);
  onMove(e);
};
const onMove = e => {
  if (!drawing.value) return;
  const p = toLocal(e);
  cursor.value = p;
  const i = hit(p);
  if (i >= 0 && !seq.value.includes(i)) {
    seq.value.push(i);
    emits('change', seq.value.join(''));
  }
};
const onUp = () => {
  if (!drawing.value) return;
  drawing.value = false;
  cursor.value = null;
  if (seq.value.length < MIN_POINTS) {
    if (seq.value.length) error.value = `점을 ${MIN_POINTS}개 이상 이어 주세요.`;
    seq.value = [];
    return;
  }
  emits('complete', seq.value.join(''));
};
const reset = (msg = '') => {
  seq.value = [];
  cursor.value = null;
  drawing.value = false;
  error.value = msg;
};
defineExpose({ reset });
</script>
<style scoped>
.pl {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
}
.pl__board {
  width: min(26rem, 72vw);
  height: auto;
  touch-action: none;
  user-select: none;
  border-radius: 1.6rem;
  background: #f4f6fb;
}
.pl__hit { fill: transparent; }
.pl__dot {
  fill: #fff;
  stroke: #b9c3d6;
  stroke-width: 3;
  transition: stroke 0.12s, fill 0.12s;
}
.pl__dot.is-on { stroke: #3182f6; fill: #e6f0ff; }
.pl__core { fill: #3182f6; }
.pl__line {
  fill: none;
  stroke: #3182f6;
  stroke-width: 6;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.85;
}
.pl--error .pl__dot.is-on, .pl--error .pl__line { stroke: #ff3b30; }
.pl--error .pl__core { fill: #ff3b30; }
.pl__hint {
  min-height: 2rem;
  font-size: 1.4rem;
  color: #6b7280;
  text-align: center;
}
.pl--error .pl__hint { color: #ff3b30; }
</style>
